import React from 'react'

import {
  AlignItems,
  BorderStyle,
  Box,
  Color,
  Display,
  DisplayPosition,
  FontWeight,
  JustifyContent,
  Text,
  TextAlign,
  ZIndex,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { CrossOut } from '../RecipeOptionsIngredients/CrossOut'

const onSpaceOrEnter = (callback: () => void) => (event: React.KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    callback()
  }
}

const SelectablePillBase = withCitrusTheme(Box, (theme) => ({
  cursor: 'pointer',
  minWidth: '160px',
  height: '40px',
  color: theme.color(Color.ColdGrey_800),
  backgroundColor: theme.color(Color.NeutralGrey_50),
  borderStyle: BorderStyle.Solid,
  borderColor: theme.color(Color.NeutralGrey_200),
  display: Display.Flex,
  justifyContent: JustifyContent.Center,
  alignItems: AlignItems.Center,
  borderRadius: '100px',
  borderWidth: '1px',
  paddingRight: theme.spacing(4),
  paddingLeft: theme.spacing(4),
  position: DisplayPosition.Relative,
  '&:hover': {
    backgroundColor: theme.color(Color.NeutralGrey_100),
  },
}))

const SelectableSoldOutPillBase = withCitrusTheme(SelectablePillBase, (theme) => ({
  color: theme.color(Color.ColdGrey_500),
}))

const SelectedPillBase = withCitrusTheme(Box, (theme) => ({
  minWidth: '160px',
  height: '40px',
  cursor: 'default',
  color: theme.color(Color.ColdGrey_800),
  backgroundColor: theme.color(Color.Secondary_50),
  borderStyle: BorderStyle.Solid,
  borderColor: theme.color(Color.Secondary_400),
  borderRadius: '100px',
  borderWidth: '2px',
  display: Display.Flex,
  justifyContent: JustifyContent.Center,
  alignItems: AlignItems.Center,
  paddingRight: theme.spacing(4),
  paddingLeft: theme.spacing(4),
}))

export const PillLabel = ({ text }: { text: string }) => (
  <Text
    size={1}
    textAlign={TextAlign.Center}
    fontWeight={FontWeight.Bold}
    style={{ whiteSpace: 'nowrap' }}
  >
    {text}
  </Text>
)

type OptionPillProps = {
  children: React.ReactNode
}

export const SelectedPill = ({ children }: OptionPillProps) => (
  <SelectedPillBase aria-current="true">{children}</SelectedPillBase>
)

type SelectablePillProps = {
  children: React.ReactNode
  onClick: () => void
  isSoldOut?: boolean
  isDisabled?: boolean
}

const getSelectablePillProps = (onClick: () => void, isDisabled: boolean) => ({
  role: 'button',
  tabIndex: isDisabled ? -1 : 0,
  onClick: () => {
    if (!isDisabled) {
      onClick()
    }
  },
  onKeyDown: onSpaceOrEnter(() => {
    if (!isDisabled) {
      onClick()
    }
  }),
  ...(isDisabled ? { 'aria-disabled': true } : {}),
})

export const SelectablePill = ({
  children,
  onClick,
  isSoldOut = false,
  isDisabled = false,
}: SelectablePillProps) =>
  isSoldOut ? (
    <SelectableSoldOutPillBase
      {...getSelectablePillProps(onClick, isDisabled)}
      style={{ cursor: isDisabled ? 'default' : 'pointer' }}
    >
      <Box
        width="75%"
        height="99%"
        color={Color.NeutralGrey_300}
        position={DisplayPosition.Absolute}
        zIndex={ZIndex.Top}
        top="0px"
        right="0px"
        bottom="0px"
        left="0px"
        style={{ margin: 'auto' }}
      >
        <CrossOut />
      </Box>
      {children}
    </SelectableSoldOutPillBase>
  ) : (
    <SelectablePillBase
      {...getSelectablePillProps(onClick, isDisabled)}
      style={{ cursor: isDisabled ? 'default' : 'pointer' }}
    >
      {children}
    </SelectablePillBase>
  )
